import React from 'react';
import { useAudio } from './hooks/useAudio';
import KBCUserProfile from './KBCUserProfile';

interface LandingPageProps {
  onStart: () => void;
  onViewRules: () => void;
  onViewLeaderboard: () => void;
  onViewAdmin?: () => void;
  onViewMyResult?: () => void;
  hasAttempted?: boolean;
  isAdmin?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({ 
  onStart, 
  onViewRules, 
  onViewLeaderboard, 
  onViewAdmin,
  onViewMyResult,
  hasAttempted = false,
  isAdmin = false 
}) => {
  const { audioMuted, toggleAudio } = useAudio();
  return (
    <div className="min-h-screen bg-gradient-to-br from-kbc-dark via-kbc-dark-accent to-kbc-dark flex items-center justify-center p-4 relative">
      {/* User Profile - Bottom Left */}
      <KBCUserProfile size="large" position="top-left" label="On the Hot Seat" />
      
      <div className="max-w-lg w-full text-center animate-fade-in">
        {/* Logo */}
        <div className="mb-8">
          <div className="w-32 h-32 mx-auto rounded-full flex items-center justify-center shadow-2xl mb-2">
            <img 
              src="/kaunbanegashipmentcocaptain.svg" 
              alt="KBC Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            KBC
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold text-maersk-blue">
            Kaun Banega CoCaptain?
          </h2>
        </div>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-white/90 mb-8 px-4">
          Test your GenAI skills for a chance to win some exciting prizes!
        </p>

        {/* CTA Buttons */}
        <div className="space-y-4 px-4">
          {!hasAttempted && (
            <button
              onClick={onStart}
              className="w-full bg-maersk-blue hover:bg-opacity-80 text-maersk-navy font-bold text-xl py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-105 active:scale-95"
            >
              Play Now
            </button>
          )}
          {hasAttempted && onViewMyResult && (
            <button
              onClick={onViewMyResult}
              className="w-full bg-white/80 hover:bg-opacity-80 text-maersk-navy font-bold text-xl py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-105 active:scale-95"
            >
              <div className="text-sm font-normal mb-1">Already Played!</div>
              <div>View My Result</div>
            </button>
          )}
          
          <button
            onClick={onViewRules}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-lg border-2 border-white/30 transition"
          >
            {/* 📋  */}
            Preview Rules & Prizes
          </button>

          <button
            onClick={onViewLeaderboard}
            className="w-full bg-kbc-gold hover:bg-kbc-amber text-maersk-navy font-semibold py-3 px-6 rounded-lg border-2 border-kbc-gold/50 transition shadow-lg"
          >
            🏆 
            View Leaderboard
          </button>

          {/* Audio Toggle */}
          <button
            onClick={toggleAudio}
            className="w-full bg-white/5 hover:bg-white/10 text-white/80 hover:text-white font-medium py-2 px-6 rounded-lg border border-white/20 transition flex items-center justify-center gap-2"
          >
            <span className="text-lg">{audioMuted ? '🔇' : '🔊'}</span>
            <span>{audioMuted ? 'Sound Off' : 'Sound On'}</span>
          </button>

          {/* Admin Button - Only visible to admin */}
          {isAdmin && onViewAdmin && (
            <button
              onClick={onViewAdmin}
              className="w-xs bg-white/5 hover:bg-white/10 text-white font-bold py-3 px-6 rounded-lg border-2 border-white/20 transition shadow-lg"
            >
              🔧 Admin Panel
            </button>
          )}
        </div>

        {/* Footer info */}
        <div className="mt-12 text-white/70 text-sm">
          <p>Powered by Shipment CoCaptain</p>
          {/* <p className="mt-2">A Maersk GenAI Experience</p> */}
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-kbc-gold/10 rounded-full blur-xl animate-pulse-slow hidden md:block"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-maersk-blue/10 rounded-full blur-xl animate-pulse-slow hidden md:block"></div>
      </div>
    </div>
  );
};

export default LandingPage;
